import { Controller } from "@hotwired/stimulus"

// Menu a tendina (Radix Select) con lo stesso aspetto dell'originale. Il valore scelto va in un campo nascosto
// (target "input") che emette "change", così i form e gli altri controller reagiscono come con un <select>.
// Con `vuoto` quel valore (es. «tutte» di «Tutte le azioni») non viene inviato.
// Dopo ogni morph (pagina: turbo:morph; un turbo-stream con method: :morph: turbo:morph-element, che risale dagli
// elementi interni) l'etichetta torna quella scritta dal server: il menu si riallinea da solo al valore del campo.
export default class extends Controller {
  static targets = ["trigger", "label", "content", "item", "input"]
  static values = { placeholder: String, vuoto: String }

  connect() {
    this.onOutside = this.onOutside.bind(this)
    this.onKey = this.onKey.bind(this)
    this.riallinea = () => this.sync()
    document.addEventListener("turbo:morph", this.riallinea)
    this.element.addEventListener("turbo:morph-element", this.riallinea)
    this.sync()
  }

  disconnect() {
    document.removeEventListener("turbo:morph", this.riallinea)
    this.element.removeEventListener("turbo:morph-element", this.riallinea)
    this.removeListeners()
  }

  toggle(event) {
    event.preventDefault()
    this.contentTarget.hidden ? this.open() : this.close()
  }

  open() {
    if (this.triggerTarget.disabled) return
    this.contentTarget.hidden = false
    this.contentTarget.dataset.state = "open"
    this.triggerTarget.setAttribute("aria-expanded", "true")
    document.addEventListener("click", this.onOutside, true)
    document.addEventListener("keydown", this.onKey)
    const current = this.itemTargets.find((i) => i.dataset.value === this.inputTarget.value) || this.itemTargets[0]
    current?.focus()
  }

  close() {
    this.contentTarget.dataset.state = "closed"
    this.contentTarget.hidden = true
    this.triggerTarget.setAttribute("aria-expanded", "false")
    this.removeListeners()
  }

  choose(event) {
    const item = event.currentTarget
    if (item.dataset.disabled !== undefined) return
    const changed = this.inputTarget.value !== item.dataset.value
    this.inputTarget.value = item.dataset.value
    this.sync()
    this.close()
    this.triggerTarget.focus()
    if (changed) {
      this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))
      this.dispatch("change", { detail: { value: item.dataset.value } })
    }
  }

  sync() {
    const value = this.inputTarget.value
    let text = null
    this.itemTargets.forEach((i) => {
      const on = i.dataset.value === value && value !== ""
      i.dataset.state = on ? "checked" : "unchecked"
      i.setAttribute("aria-selected", String(on))
      const check = i.querySelector("[data-indicator]")
      if (check) check.hidden = !on
      // data-etichetta: il testo del pulsante quando differisce da quello della voce (pages/Readiness.js: solo il nome)
      if (on) text = i.dataset.etichetta ?? i.querySelector("[data-item-text]")?.textContent ?? i.textContent
    })
    // `vuoto`: il valore che non si invia (Registro.js: v === "tutte" ? "" : v): il campo disattivato non entra né nel
    // form né nell'indirizzo del download (FormData).
    if (this.hasVuotoValue) this.inputTarget.disabled = value === this.vuotoValue
    this.labelTarget.textContent = text ?? this.placeholderValue
    if (text === null) this.triggerTarget.setAttribute("data-placeholder", "")
    else this.triggerTarget.removeAttribute("data-placeholder")
  }

  onOutside(event) {
    if (!this.element.contains(event.target)) this.close()
  }

  onKey(event) {
    const items = this.itemTargets.filter((i) => i.dataset.disabled === undefined && !i.hidden)
    const idx = items.indexOf(document.activeElement)
    if (event.key === "Escape") { this.close(); this.triggerTarget.focus() }
    else if (event.key === "ArrowDown") { event.preventDefault(); items[Math.min(idx + 1, items.length - 1)]?.focus() }
    else if (event.key === "ArrowUp") { event.preventDefault(); items[Math.max(idx - 1, 0)]?.focus() }
    else if (event.key === "Enter" || event.key === " ") { if (idx >= 0) { event.preventDefault(); items[idx].click() } }
  }

  removeListeners() {
    document.removeEventListener("click", this.onOutside, true)
    document.removeEventListener("keydown", this.onKey)
  }
}
